import React, { Fragment } from 'react';
import { graphql } from 'gatsby';
import {
  Banner, Button, useBreakpoint, RichText,
} from '@printdeal/ui-library';
import { Markdown } from '@printdeal/markdown/react';
import clsx from 'clsx';
import { Image, Source } from '@unpic/react';
import type { NodeData } from '@contentful/rich-text-types';
import { useLocation } from '../../hooks/useSafeLocation';
import type { ContentfulStaticBanner as BannerData } from '../../types/banner';

const mapGraphqlToProps = (response: BannerData) => {
  const {
    alignment,
    bannerTitle,
    buttonKind,
    buttonText,
    buttonLink,
    media,
    mediaMobile,
    text,
    contentColor,
    bannerLink,
  } = response;

  return {
    __typename: 'ContentfulModuleFullWidth' as const,
    bannerTitle: bannerTitle || '',
    contentColor,
    alignment,
    buttonText: buttonText || undefined,
    buttonKind,
    buttonLink: buttonLink || undefined,
    media: media || undefined,
    mediaMobile: mediaMobile || undefined,
    text,
    bannerLink,
  };
};

const ModuleFullWidth = (graphqlProps: { data: NodeData }) => {
  const { data: { target, clickHandler } } = graphqlProps;
  const {
    bannerTitle,
    contentColor,
    alignment,
    buttonText,
    buttonLink,
    media,
    mediaMobile,
    text,
    bannerLink,
  } = mapGraphqlToProps(target);
  const isMobile = useBreakpoint('max-lg');
  const { origin } = useLocation();

  if (!media || !mediaMobile) {
    return null;
  }

  return (
    <Banner
      title={bannerTitle}
      variant={isMobile ? 'vertical' : 'horizontal'}
      image={(
        <picture>
          {['avif', 'webp', null].map((format) => (
            <Fragment key={format || 'default'}>
              <Source
                src={`${mediaMobile.url}?fm=${format || ''}`}
                media="(max-width: 991.5px)"
                width={mediaMobile.width}
                height={mediaMobile.height}
                type={format && `image/${format}`}
                breakpoints={[320, 640, 950]}
              />
              <Source
                src={`${media.url}?fm=${format || ''}`}
                media="(min-width: 992px)"
                width={media.width}
                height={media.height}
                type={format && `image/${format}`}
                breakpoints={[992, 1280]}
              />
            </Fragment>
          ))}
          <Image
            src={isMobile ? mediaMobile.url : media.url}
            layout="fullWidth"
            alt={bannerTitle}
            priority
            unstyled
          />
        </picture>
      )}
      justify={alignment === 'Right' ? 'end' : 'start'}
      link={(buttonLink || bannerLink)
        ? <a href={buttonLink || bannerLink} onClick={clickHandler}>{bannerTitle}</a>
        : undefined}
      button={buttonText ? <Button as="span" variant="secondary">{buttonText}</Button> : undefined}
      className={clsx(contentColor && 'pd-text-dim-800')}
    >
      {text && (
        <RichText>
          <Markdown origin={origin}>{text.text}</Markdown>
        </RichText>
      )}
    </Banner>
  );
};

export default ModuleFullWidth;

/** @knipignore */
export const query = graphql`
  fragment ModuleFullWidth on ContentfulModuleFullWidth {
    __typename
    contentful_id
    contentColor
    text {
      text
    }
    title
    bannerTitle
    alignment
    buttonLink: buttonUrl
    buttonText
    buttonKind
    media {
      url
      title
      width
      height
    }
    mediaMobile {
      url
      title
      width
      height
    }
    bannerLink
  }
`;
